import { API_BASE_URL } from '../config/api'
import { getAuthToken } from '../utils/authUtils'
import { firebaseAuthService } from '../services/firebaseAuthService'

export interface VolunteerProfileData {
  firstName: string
  lastName: string
  phone: string
  street: string
  city: string
  state: string
  zipCode: string
  vehicleType: 'CAR' | 'TRUCK' | 'VAN' | 'MOTORCYCLE' | 'BICYCLE' | 'WALKING'
  availability: string[]
  latitude?: number
  longitude?: number
}

// Create or update volunteer profile during onboarding
export async function createVolunteerProfile(profileData: VolunteerProfileData): Promise<any> {
  try {
    // Ensure we have a fresh token before making the API call
    console.log('🔍 Getting auth token for volunteer profile creation...')
    let token = await getAuthToken()
    
    // If no token found, try to get a fresh one from Firebase
    if (!token) {
      console.log('⚠️ No token in storage, getting fresh Firebase token...')
      const firebaseToken = await firebaseAuthService.getFirebaseToken(true)
      if (firebaseToken) {
        localStorage.setItem('firebaseToken', firebaseToken)
        token = firebaseToken
        console.log('✅ Fresh Firebase token obtained and stored')
      }
    }
    
    if (!token) {
      throw new Error('No authentication token available. Please log in again.')
    }
    
    console.log('✅ Token available, making API request...')
    const response = await fetch(`${API_BASE_URL}/api/volunteers/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create volunteer profile')
    }
    
    return data.data
  } catch (error) {
    console.error('Error creating volunteer profile:', error)
    throw error
  }
}

export async function updateVolunteerHomeLocation(
  latitude: number,
  longitude: number
): Promise<void> {
  const token = await getAuthToken()
  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await fetch(`${API_BASE_URL}/api/volunteers/profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ latitude, longitude })
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as any).error || 'Failed to save home location')
  }
}

// Get volunteer profile
export async function getVolunteerProfile(): Promise<any> {
  try {
    // Get token asynchronously
    const token = await getAuthToken()
    if (!token) {
      throw new Error('No authentication token available')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/volunteers/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch volunteer profile')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching volunteer profile:', error)
    return null
  }
}

export interface VolunteerContactProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  userType: string
  profileImage?: string | null
  volunteerProfile?: {
    city?: string
    state?: string
    vehicleType?: string
  } | null
}

// Fetch a volunteer's contact/profile info (supplier/food bank/admin or self)
export async function getVolunteerById(volunteerUserId: string): Promise<VolunteerContactProfile> {
  const token = await getAuthToken()
  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await fetch(`${API_BASE_URL}/api/volunteers/${volunteerUserId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch volunteer profile')
  }

  return data.data
}
